/** @graphql experimental **/

import gql from 'graphql-tag';
import { useCallback } from 'react';

import { ConfluenceSiteAri } from '@atlassian/ari';

import { useLazyQueryInterop } from '@confluence/apollo-relay-interop';
import { useSessionData } from '@confluence/session-data';

import type {
	ShareAndRestrictDialogInviteCapabilitiesQuery as ShareAndRestrictDialogInviteCapabilitiesQueryType,
	ShareAndRestrictDialogInviteCapabilitiesQueryVariables,
} from './__types__/ShareAndRestrictDialogInviteCapabilitiesQuery';

const useInviteCapabilityShareAndRestrictDialogQuery = gql`
	query useInviteCapabilityShareAndRestrictDialogQuery($resourceARI: String!) @experimental {
		experimentalInvitePermissions(resourceARI: $resourceARI) {
			directInviteMode
			directInviteDomains
			invitePendingApprovalMode
		}
	}
`;

export const useInviteCapability = () => {
	const { cloudId: siteId } = useSessionData();
	const resourceARI = ConfluenceSiteAri.create({ siteId }).toString();

	const [loadInviteCapabilities, { data, loading, error, called }] = useLazyQueryInterop<
		ShareAndRestrictDialogInviteCapabilitiesQueryType,
		ShareAndRestrictDialogInviteCapabilitiesQueryVariables
	>(useInviteCapabilityShareAndRestrictDialogQuery, {
		variables: { resourceARI },
	});

	const canUserInvite = useCallback(
		(emails: string[]) => {
			if (loading || error) {
				return false;
			}

			if (
				data?.experimentalInvitePermissions?.invitePendingApprovalMode === 'ANYONE' ||
				data?.experimentalInvitePermissions?.directInviteMode === 'ANYONE'
			) {
				return true;
			}

			if (
				data?.experimentalInvitePermissions?.directInviteMode === 'DOMAIN_RESTRICTED' &&
				emails.some((email) => {
					const domain = email.split('@')[1];
					return data?.experimentalInvitePermissions?.directInviteDomains?.includes(domain);
				})
			) {
				return true;
			}

			return false;
		},
		[data, loading, error],
	);

	return {
		loadInviteCapabilities,
		canUserInvite,
		called,
	};
};
